"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type SyntheticEvent,
  type TouchEvent,
} from "react";
import { useMediaQuery } from "react-responsive";
import {
  Box as MantineBox,
  Button,
  Image as MantineImage,
  Text as MantineText,
  Tooltip,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";
import { IconChevronDown, IconPhotoSearch, IconShare } from "@tabler/icons-react";

import AuthSignInButton from "@/components/AuthSignInButton";
import Carousel from "@/components/Carousel";
import Link from "@/components/Link";
import PriceBadge from "@/components/PriceBadge";
import PricePositionBar from "@/components/PricePositionBar";
import { DuplicateListings } from "@/components/ItemSlideIn/DuplicateListings";
import { PriceDropCta } from "@/components/ItemSlideIn/PriceDropCta";
import CouponIcon from "@/icons/Coupon";
import HeartIcon from "@/icons/Heart";
import ReportIcon from "@/icons/Report";
import { formatDateInTimezone } from "@/lib/client/utils";
import { formatPriceWithSymbol, getDisplayedPriceNumeric } from "@/lib/shared/currency";
import { englishRegions } from "@/lib/shared/constants";
import { isCouponExpired } from "@/lib/shared/date";
import { formatSize, generateBrandSlug } from "@/lib/shared/utils";

import styles from "./styles.module.scss";

const CT_CONDITION_LABELS: Record<number, string> = {
  5: "New",
  4: "Like new",
  3: "Good",
  2: "Used",
  1: "Heavily used",
};

const formatConditionLabel = (value: string | null | undefined): string | null => {
  const condition = value?.trim();
  if (!condition) return null;

  if (!/^[a-z0-9]+(?:_[a-z0-9]+)+$/i.test(condition)) return condition;

  return condition
    .replace(/^is_/i, "")
    .split("_")
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
    .join(" ");
};

type PriceInsightData = {
  hasStats: boolean;
  percentile: number;
  comparisonName?: string;
  insight?: { label: string; color: string } | null;
};

type DuplicateData = {
  items: SearchResultItem[];
  loading: boolean;
  hasSearched: boolean;
  error: boolean;
  retry: () => void;
};

type ItemHeaderActionsProps = {
  hasImage: boolean;
  isSold: boolean;
  isSignedIn: boolean | undefined;
  isFavorited: boolean;
  shareLabel: string;
  onFindSimilar: () => void;
  onFavorite: (event: SyntheticEvent) => void;
  onShare: () => void;
  onSignedOutFavorite?: (event: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
};

export const ItemHeaderActions = ({
  hasImage,
  isSold,
  isSignedIn,
  isFavorited,
  shareLabel,
  onFindSimilar,
  onFavorite,
  onShare,
  onSignedOutFavorite,
  className,
}: ItemHeaderActionsProps) => (
  <MantineBox className={`${styles.headerActions}${className ? ` ${className}` : ""}`}>
    {hasImage && (
      <MantineUnstyledButton className={styles.headerButton} onClick={onFindSimilar}>
        <IconPhotoSearch size={18} />
        FIND MORE
      </MantineUnstyledButton>
    )}
    {!isSold &&
      (isSignedIn ? (
        <MantineUnstyledButton
          className={`${styles.headerButton} ${styles.saveButton}`}
          onClick={onFavorite}
          data-selected={isFavorited}
          title={isFavorited ? "Remove from favorites" : "Save item"}
        >
          <HeartIcon selected={isFavorited} locked={false} />
          SAVE
        </MantineUnstyledButton>
      ) : (
        <AuthSignInButton>
          {/* eslint-disable-next-line no-restricted-syntax -- Clerk must clone a native element. */}
          <button
            className={`${styles.headerButton} ${styles.saveButton}`}
            onClick={(event) => {
              event.stopPropagation();
              onSignedOutFavorite?.(event);
            }}
            data-selected="false"
            title="Sign in to save"
          >
            <HeartIcon selected={false} locked={true} />
            SAVE
          </button>
        </AuthSignInButton>
      ))}
    <MantineUnstyledButton className={styles.headerButton} onClick={onShare}>
      <IconShare size={18} />
      {shareLabel}
    </MantineUnstyledButton>
  </MantineBox>
);

type ItemGalleryProps = {
  images: Array<string | null | undefined>;
  itemId?: number;
  title?: string | null;
  layout?: "slideout" | "page";
  loading?: boolean;
};

export const ItemGallery = ({
  images,
  itemId,
  title,
  layout = "slideout",
  loading = false,
}: ItemGalleryProps) => {
  const validImages = useMemo(() => images.filter((image): image is string => !!image), [images]);
  const zoomContainerRef = useRef<HTMLDivElement>(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
  const [isZoomPanning, setIsZoomPanning] = useState(false);
  const zoomPinchRef = useRef<{ dist: number; initScale: number } | null>(null);
  const zoomPanRef = useRef<{
    startX: number;
    startY: number;
    initOffX: number;
    initOffY: number;
  } | null>(null);
  const zoomMouseDownPos = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setZoomScale(1);
    setZoomOffset({ x: 0, y: 0 });
  }, [itemId]);

  useEffect(() => {
    const element = zoomContainerRef.current;
    if (!element) return;

    const preventPageZoom = (event: globalThis.TouchEvent) => {
      if (event.touches.length >= 2) event.preventDefault();
    };

    element.addEventListener("touchmove", preventPageZoom, { passive: false });
    return () => element.removeEventListener("touchmove", preventPageZoom);
  }, []);

  const clampZoom = (x: number, y: number, scale: number) => {
    const element = zoomContainerRef.current;
    if (!element || scale <= 1) return { x: 0, y: 0 };

    const maxX = ((scale - 1) / 2) * element.offsetWidth;
    const maxY = ((scale - 1) / 2) * element.offsetHeight;
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  };

  const toggleZoom = () => {
    setZoomScale((currentScale) => {
      if (currentScale >= 2) {
        setZoomOffset({ x: 0, y: 0 });
        return 1;
      }
      return 2;
    });
  };

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button, a")) return;
    zoomMouseDownPos.current = { x: event.clientX, y: event.clientY };

    if (zoomScale > 1) {
      event.stopPropagation();
      setIsZoomPanning(true);
      zoomPanRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        initOffX: zoomOffset.x,
        initOffY: zoomOffset.y,
      };
    }
  };

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!zoomPanRef.current || zoomScale <= 1) return;
    event.stopPropagation();
    setZoomOffset(
      clampZoom(
        zoomPanRef.current.initOffX + event.clientX - zoomPanRef.current.startX,
        zoomPanRef.current.initOffY + event.clientY - zoomPanRef.current.startY,
        zoomScale
      )
    );
  };

  const handleMouseUp = (event: MouseEvent<HTMLDivElement>) => {
    setIsZoomPanning(false);
    zoomPanRef.current = null;
    const mouseDownPosition = zoomMouseDownPos.current;
    zoomMouseDownPos.current = null;
    if (!mouseDownPosition) return;

    if (Math.hypot(event.clientX - mouseDownPosition.x, event.clientY - mouseDownPosition.y) < 5) {
      toggleZoom();
    }
  };

  const handleMouseLeave = () => {
    setIsZoomPanning(false);
    zoomPanRef.current = null;
    zoomMouseDownPos.current = null;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) {
      return;
    }
    event.preventDefault();
    toggleZoom();
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 2) {
      event.stopPropagation();
      const dx = event.touches[1].clientX - event.touches[0].clientX;
      const dy = event.touches[1].clientY - event.touches[0].clientY;
      zoomPinchRef.current = { dist: Math.hypot(dx, dy), initScale: zoomScale };
    } else if (event.touches.length === 1 && zoomScale > 1) {
      event.stopPropagation();
      zoomPanRef.current = {
        startX: event.touches[0].clientX,
        startY: event.touches[0].clientY,
        initOffX: zoomOffset.x,
        initOffY: zoomOffset.y,
      };
    }
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 2 && zoomPinchRef.current) {
      event.stopPropagation();
      const dx = event.touches[1].clientX - event.touches[0].clientX;
      const dy = event.touches[1].clientY - event.touches[0].clientY;
      const scale = Math.min(
        4,
        Math.max(
          1,
          zoomPinchRef.current.initScale * (Math.hypot(dx, dy) / zoomPinchRef.current.dist)
        )
      );
      setZoomScale(scale);
      if (scale <= 1) setZoomOffset({ x: 0, y: 0 });
    } else if (event.touches.length === 1 && zoomScale > 1 && zoomPanRef.current) {
      event.stopPropagation();
      setZoomOffset(
        clampZoom(
          zoomPanRef.current.initOffX + event.touches[0].clientX - zoomPanRef.current.startX,
          zoomPanRef.current.initOffY + event.touches[0].clientY - zoomPanRef.current.startY,
          zoomScale
        )
      );
    }
  };

  const handleTouchEnd = () => {
    zoomPinchRef.current = null;
    zoomPanRef.current = null;
    setZoomScale((currentScale) => {
      if (currentScale < 1.1) {
        setZoomOffset({ x: 0, y: 0 });
        return 1;
      }
      return currentScale;
    });
  };

  return (
    <MantineBox className={styles.gallery} data-layout={layout}>
      <MantineBox
        ref={zoomContainerRef}
        className={styles.zoomContainer}
        role="button"
        tabIndex={0}
        aria-label={zoomScale > 1 ? "Reset item image zoom" : "Zoom item image"}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ cursor: isZoomPanning ? "grabbing" : zoomScale > 1 ? "grab" : "zoom-in" }}
      >
        <MantineBox
          className={styles.galleryTransform}
          style={{
            transform: `scale(${zoomScale}) translate(${zoomOffset.x / zoomScale}px, ${zoomOffset.y / zoomScale}px)`,
            transition: isZoomPanning ? "none" : "transform 0.2s ease",
            transformOrigin: "center center",
          }}
        >
          <MantineBox className={styles.galleryViewport}>
            {validImages.length ? (
              <Carousel key={`${itemId}-${String(loading)}`} images={validImages} />
            ) : (
              <MantineImage
                src="/kurb_broken_image.svg"
                className={styles.galleryImage}
                alt={title ?? "Item image"}
                data-broken="true"
              />
            )}
          </MantineBox>
        </MantineBox>
      </MantineBox>
    </MantineBox>
  );
};

type ItemDetailsProps = {
  item: SearchResultItem;
  price?: string | number;
  currencyCode?: string;
  description?: string;
  descriptionLoading?: boolean;
  condition?: string | null;
  priceInsights: PriceInsightData;
  duplicates: DuplicateData;
  proxies?: ProxyCountry[];
  isFavorited: boolean;
  isMonitored: boolean;
  isProUser: boolean | undefined;
  translationLoading: boolean;
  translated: boolean;
  timestampDisplay?: string | null;
  onAppraise: () => void;
  onBuy: () => void;
  onOpenPriceMonitor: () => void;
  onOpenSizeGuide: () => void;
  onOpenWarning: () => void;
  onTranslate: () => void;
  onNavigate?: () => void;
};

const getCouponString = (coupon: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = coupon[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return null;
};

export const ItemDetails = ({
  item,
  price = item.price,
  currencyCode = item.currency,
  description,
  descriptionLoading = false,
  condition,
  priceInsights,
  duplicates,
  proxies,
  isFavorited,
  isMonitored,
  isProUser,
  translationLoading,
  translated,
  timestampDisplay,
  onAppraise,
  onBuy,
  onOpenPriceMonitor,
  onOpenSizeGuide,
  onOpenWarning,
  onTranslate,
  onNavigate,
}: ItemDetailsProps) => {
  const isMobile = useMediaQuery({ query: "(max-width: 450px)" });
  const [showCoupons, setShowCoupons] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionCanExpand, setDescriptionCanExpand] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const isSold = !!item.isSold;
  const canTranslate = !!item.region && !englishRegions.includes(item.region);
  const translateLabel = translationLoading
    ? "Translating…"
    : translated
      ? "Translated"
      : "Translate";
  const translateAriaLabel = description ? translateLabel : `${translateLabel} title`;
  const displaySize = item.ctsize || item.size || null;
  const rawDisplayCondition =
    condition ??
    (item.ctcondition != null
      ? (CT_CONDITION_LABELS[item.ctcondition] ?? item.condition)
      : item.condition) ??
    null;
  const displayCondition = formatConditionLabel(rawDisplayCondition);
  const activeCoupons = useMemo(
    () =>
      (item.vendor?.coupons ?? []).filter((coupon) => {
        const expiry = getCouponString(coupon, ["enddate", "couponexpiry"]);
        return !isCouponExpired(expiry);
      }),
    [item.vendor?.coupons]
  );
  const proxyCountry = proxies?.find(
    (proxy) => proxy.countryname?.toLowerCase() === item.region?.toLowerCase()
  );
  const displayedPrice =
    item.rentable && item.priceusd === 0
      ? "FOR RENT"
      : formatPriceWithSymbol({
          price,
          currencyCode,
          priceUsd: item.priceusd,
          roundToFive: false,
          rawPrice: item.rawPrice,
        });
  const displayedPriceNumeric =
    item.rentable && item.priceusd === 0
      ? 0
      : getDisplayedPriceNumeric({
          price,
          currencyCode,
          abbreviate: true,
          priceUsd: item.priceusd,
          roundToFive: false,
          rawPrice: item.rawPrice,
        });

  useEffect(() => {
    setShowCoupons(false);
  }, [item.id]);

  useEffect(() => {
    setDescriptionExpanded(false);
    setDescriptionCanExpand(false);
  }, [description, item.id]);

  useEffect(() => {
    const node = descriptionRef.current;
    if (!node || !description || descriptionExpanded) return;

    const updateOverflow = () => {
      setDescriptionCanExpand(node.scrollHeight > node.clientHeight + 1);
    };

    updateOverflow();
    window.addEventListener("resize", updateOverflow);
    return () => window.removeEventListener("resize", updateOverflow);
  }, [description, descriptionExpanded, descriptionLoading]);

  return (
    <MantineBox className={styles.details}>
      <MantineBox className={styles.pricingSummary}>
        <MantineBox className={styles.brandRow}>
          {item.brand?.label && (
            <Link
              href={`/brands/${generateBrandSlug(item.brand.label)}`}
              className={styles.brandLink}
              onClick={onNavigate}
            >
              {item.brand.label}
            </Link>
          )}
          {(item.rentable || item.saletype === 2 || item.blacklisted) && (
            <MantineBox className={styles.brandChips}>
              {item.saletype === 2 && (
                <MantineBox className={styles.neutralChip}>Auction</MantineBox>
              )}
              {item.rentable && <MantineBox className={styles.neutralChip}>Rentable</MantineBox>}
              {item.blacklisted && (
                <Tooltip
                  label="Warning: This item has been blocklisted for potential authenticity or dropshipping concerns."
                  openDelay={200}
                  withArrow
                  zIndex={10000}
                  multiline
                  maw={220}
                  disabled={isMobile}
                >
                  <MantineBox
                    className={styles.warningChip}
                    role={isMobile ? "button" : undefined}
                    tabIndex={isMobile ? 0 : undefined}
                    onClick={isMobile ? onOpenWarning : undefined}
                    onKeyDown={
                      isMobile
                        ? (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onOpenWarning();
                            }
                          }
                        : undefined
                    }
                  >
                    WARNING
                  </MantineBox>
                </Tooltip>
              )}
            </MantineBox>
          )}
        </MantineBox>

        <MantineBox className={styles.priceRow}>
          <MantineText className={`${styles.price} ${isSold ? styles.sold : ""}`}>
            {isSold && "Sold"}
            <MantineText span fw={700} className={styles.priceValue}>
              {displayedPrice}
              <PriceBadge
                priceChange={{
                  oldPriceUsd: item.old_priceusd,
                  newPriceUsd: item.new_priceusd,
                  changeTimestamp: item.change_timestamp,
                }}
                isSold={isSold}
                displayedPrice={displayedPriceNumeric}
              />
            </MantineText>
          </MantineText>
          <MantineBox className={styles.priceActions}>
            {isProUser && (
              <MantineUnstyledButton className={styles.textAction} onClick={onAppraise}>
                APPRAISE
              </MantineUnstyledButton>
            )}
            {!!activeCoupons.length && (
              <MantineUnstyledButton
                className={styles.couponToggle}
                onClick={() => setShowCoupons((current) => !current)}
                aria-expanded={showCoupons}
              >
                Check discount codes
                <IconChevronDown
                  size={14}
                  style={{
                    transform: showCoupons ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 200ms ease",
                    flexShrink: 0,
                  }}
                />
              </MantineUnstyledButton>
            )}
          </MantineBox>
        </MantineBox>
      </MantineBox>

      {!!activeCoupons.length && showCoupons && (
        <MantineBox className={styles.coupons}>
          {activeCoupons.map((coupon, index) => {
            const code = getCouponString(coupon, ["couponcode"]);
            const detail = getCouponString(coupon, ["coupondetail", "coupondescription"]);
            const expiry = getCouponString(coupon, ["enddate", "couponexpiry"]);
            return (
              <MantineBox key={code || index} className={styles.couponItem}>
                <CouponIcon />
                <MantineText span fw={700} className={styles.couponCode}>
                  {code || "N/A"}
                </MantineText>
                {detail && <MantineText className={styles.couponDetail}>{detail}</MantineText>}
                {expiry && (
                  <MantineText className={styles.couponExpiry}>
                    {formatDateInTimezone(expiry, "America/Los_Angeles")}
                  </MantineText>
                )}
              </MantineBox>
            );
          })}
        </MantineBox>
      )}

      {priceInsights.hasStats && (
        <PricePositionBar
          percentile={priceInsights.percentile}
          comparisonName={priceInsights.comparisonName}
          insight={priceInsights.insight}
        />
      )}

      <MantineBox className={styles.purchaseActions}>
        {item.url && (
          <Button
            component="a"
            href={item.url}
            target="_blank"
            rel="noreferrer"
            variant="primary"
            size="lg"
            classNames={{ root: styles.buyButton, label: styles.buyButtonLabel }}
            onClick={onBuy}
          >
            {item.rentable && item.priceusd === 0
              ? `Rent from ${item.vendor?.label || "vendor"}`
              : `Buy on ${item.vendor?.label || "seller"}`}
          </Button>
        )}

        <DuplicateListings {...duplicates} />

        <PriceDropCta
          isFavorited={isFavorited}
          isMonitored={isMonitored}
          onOpenPriceMonitor={onOpenPriceMonitor}
        />
      </MantineBox>

      {displayCondition && (
        <MantineText className={`${styles.condition} ${styles.metadataHeading}`}>
          Condition:{" "}
          <MantineText span fw={400}>
            {displayCondition}
          </MantineText>
        </MantineText>
      )}
      <MantineBox className={styles.metadataRow}>
        <MantineBox className={styles.sizeInfo}>
          {displaySize && (
            <>
              <MantineText span className={styles.metadataHeading}>
                Size:&nbsp;
                <MantineText span fw={400}>
                  {formatSize(displaySize, item.itemtype)}
                </MantineText>
              </MantineText>
              <MantineUnstyledButton
                type="button"
                className={styles.textAction}
                onClick={onOpenSizeGuide}
                aria-label="Open sizing guide"
              >
                SIZE GUIDE
              </MantineUnstyledButton>
            </>
          )}
        </MantineBox>
      </MantineBox>

      {item.region && (
        <MantineBox className={styles.locationRow}>
          <MantineText span className={`${styles.locationText} ${styles.metadataHeading}`}>
            Location:{" "}
            <MantineText span fw={400}>
              {item.region}
            </MantineText>
          </MantineText>
          <Link
            href={
              proxyCountry
                ? `/proxies?country=${encodeURIComponent(proxyCountry.countryname)}`
                : "/proxies"
            }
            className={styles.textAction}
            onClick={onNavigate}
            target="_blank"
            rel="noopener noreferrer"
          >
            PROXY OPTIONS
          </Link>
        </MantineBox>
      )}

      {(description || canTranslate) && (
        <MantineBox className={styles.descriptionHeader}>
          {description && (
            <MantineText component="h3" className={styles.descriptionHeading}>
              DESCRIPTION
            </MantineText>
          )}
          {canTranslate && (
            <MantineUnstyledButton
              type="button"
              className={styles.textAction}
              aria-label={translateAriaLabel}
              onClick={translated ? undefined : onTranslate}
              data-loading={translationLoading}
              data-translated={translated}
            >
              {translateLabel}
            </MantineUnstyledButton>
          )}
        </MantineBox>
      )}

      {!descriptionLoading && description && (
        <MantineBox className={styles.descriptionBlock}>
          <MantineBox
            ref={descriptionRef}
            id={`item-description-${item.id}`}
            className={`${styles.description} ${
              !descriptionExpanded ? styles.descriptionPreview : ""
            }`}
            data-can-expand={descriptionCanExpand || undefined}
            dangerouslySetInnerHTML={{ __html: description }}
          />
          {descriptionCanExpand && (
            <MantineUnstyledButton
              type="button"
              className={styles.descriptionToggle}
              aria-label={
                descriptionExpanded ? "Collapse full description" : "Show full description"
              }
              aria-controls={`item-description-${item.id}`}
              aria-expanded={descriptionExpanded}
              data-expanded={descriptionExpanded}
              onClick={() => setDescriptionExpanded((expanded) => !expanded)}
            >
              <IconChevronDown size={20} aria-hidden="true" />
            </MantineUnstyledButton>
          )}
        </MantineBox>
      )}

      {timestampDisplay && (
        <MantineText className={styles.timestamp}>{timestampDisplay}</MantineText>
      )}
    </MantineBox>
  );
};

export const ItemReportButton = ({ onClick }: { onClick: () => void }) => (
  <MantineBox className={styles.reportButtonWrapper}>
    <Button
      className={styles.reportButton}
      onClick={onClick}
      variant="unstyled"
      leftSection={<ReportIcon />}
    >
      Report this item
    </Button>
  </MantineBox>
);
